import { Button, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'

/**
 * The local host's library download (`backend/local-host/src/library-route.ts`). Same origin as the app. The
 * zip itself is still a plain download, so the browser's own UI shows progress on a multi-GB file, which a
 * fetch into memory could not; only the packaging before it is driven from here (GH #2394).
 */
const libraryDownloadUrl = '/api/local/library.zip'
const prepareUrl = '/api/local/library/prepare'
const statusUrl = '/api/local/library/status'

type Phase = 'idle' | 'packaging' | 'downloading' | 'failed'

/** Joni's copy (GH #2394), one line per state, read out politely */
const statusLines: Record<Phase, string> = {
  idle: '',
  packaging: 'Packaging your library. This can take a minute; you can keep using Tunasong.',
  downloading: 'Your library is downloading.',
  failed: "Couldn't package your library. Nothing changed.",
}

/** The host's packaging state, or undefined when it cannot say */
const readHostState = async () => {
  const response = await fetch(statusUrl, { cache: 'no-store' })
  if (!response.ok) {
    return undefined
  }
  const body = (await response.json()) as { state?: unknown }
  return typeof body.state === 'string' ? body.state : undefined
}

/** Hand the prepared zip to the browser's download manager without leaving the app */
const startBrowserDownload = () => {
  const link = document.createElement('a')
  link.href = libraryDownloadUrl
  link.download = ''
  document.body.append(link)
  link.click()
  link.remove()
}

export interface LocalLibraryDownloadProps {
  /** How often to ask the host while it packages. Joni's 2 s; shorter in tests. */
  pollMs?: number
  /** How long "Your library is downloading." stays before the button is back. Joni's 5 s; shorter in tests. */
  readyMs?: number
}

/**
 * A local host has no export workflow — every cloud control would 501 and the list would poll an endpoint
 * that is not there. What it offers instead is the whole library as one file, for import into Tunasong
 * Cloud (front-door spec §4.2; copy by Joni, spec §6.1).
 *
 * Packaging a large library takes a while, so the button asks the host to prepare the zip, says
 * "Packaging…" while it polls, and starts the download once the zip is ready (GH #2394, Joni's states):
 * - a second click while a request is in flight, or while packaging, sends nothing;
 * - 409 means another tab or click already started it: shown as Packaging, never as an error;
 * - a failure is said inline, with Retry;
 * - the button stays focusable while busy, so focus does not drop to the page;
 * - packaging continues on the host if the user leaves; coming back resumes Packaging.
 */
export const LocalLibraryDownload: FC<LocalLibraryDownloadProps> = props => {
  const { pollMs = 2000, readyMs = 5000 } = props
  const [phase, setPhase] = useState<Phase>('idle')
  /** A click's prepare request is in flight; a double click must not send a second one */
  const requesting = useRef(false)

  const download = () => {
    startBrowserDownload()
    setPhase('downloading')
  }

  /** A poll's answer. Ignored once packaging has ended, so two polls in flight cannot start two downloads. */
  const handleHostState = useEffectEvent((state: string | undefined) => {
    if (phase !== 'packaging') {
      return
    }
    if (state === 'ready') {
      download()
      return
    }
    if (state === 'failed' || state === undefined) {
      setPhase('failed')
      return
    }
    /** Downloaded from another tab: nothing is waiting any more */
    if (state === 'idle') {
      setPhase('idle')
    }
  })

  useEffect(() => {
    if (phase !== 'packaging') {
      return
    }
    const timer = setInterval(() => {
      void readHostState().then(handleHostState, () => handleHostState(undefined))
    }, pollMs)
    return () => clearInterval(timer)
  }, [phase, pollMs])

  useEffect(() => {
    if (phase !== 'downloading') {
      return
    }
    const timer = setTimeout(() => setPhase('idle'), readyMs)
    return () => clearTimeout(timer)
  }, [phase, readyMs])

  /** Coming back to a host that is still packaging picks the wait up again */
  const handleInitialState = useEffectEvent((state: string | undefined) => {
    if (state === 'packaging' && phase === 'idle') {
      setPhase('packaging')
    }
  })
  useEffect(() => {
    void readHostState().then(handleInitialState, () => undefined)
  }, [])

  const handleDownload = async () => {
    if (phase === 'packaging' || requesting.current) {
      return
    }
    requesting.current = true
    try {
      const response = await fetch(prepareUrl, { method: 'POST' })
      if (response.status === 202 || response.status === 409) {
        setPhase('packaging')
        return
      }
      if (response.ok) {
        /** A zip prepared earlier is already waiting */
        download()
        return
      }
      setPhase('failed')
    } catch {
      setPhase('failed')
    } finally {
      requesting.current = false
    }
  }

  const packaging = phase === 'packaging'

  return (
    <div>
      {/* Read before the action it explains */}
      <Typography variant="body2" gutterBottom>
        Saves everything in this Tunasong as one file. Then sign in to Tunasong Cloud and open Profile → Imports to
        bring it in. A large library can take a minute to package; your browser starts the download when it&apos;s
        ready.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        disabled={packaging}
        focusableWhenDisabled
        aria-busy={packaging}
        onClick={() => void handleDownload()}
      >
        {packaging ? 'Packaging…' : 'Download library'}
      </Button>
      <Typography variant="body2" role="status">
        {statusLines[phase]}
      </Typography>
      {phase === 'failed' ? (
        <Button variant="text" color="primary" onClick={() => void handleDownload()}>
          Retry
        </Button>
      ) : null}
    </div>
  )
}
